import React, { useEffect, useState } from "react";
import { Scale, RefreshCw, Mail, AlertTriangle, CheckCircle2, FileText, EyeOff, Eye, Send, Link2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "reviewed", "sent", "dismissed"];
const CLS = { new: "bg-rose-50 text-rose-700 border-rose-200", reviewed: "bg-amber-50 text-amber-700 border-amber-200", sent: "bg-emerald-50 text-emerald-700 border-emerald-200", dismissed: "bg-slate-100 text-slate-500 border-slate-200" };

export default function AdminLegalAlerts() {
  const [status, setStatus] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [checking, setChecking] = useState(false);
  const [openDraft, setOpenDraft] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState("");

  const act = async (id, path, okMsg) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/legal-alerts/${id}/${path}`, path === "send" ? { text: drafts[id] ?? alerts.find((a) => a.id === id)?.draft_text } : {});
      toast.success(okMsg(data));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusyId(""); }
  };

  const load = () => Promise.all([api.get("/admin/legal-alerts/status"), api.get("/admin/legal-alerts")]).then(([s, a]) => { setStatus(s.data); setAlerts(a.data); });
  useEffect(() => { load(); }, []);

  const checkNow = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/admin/legal-alerts/check-now");
      if (data.error) toast.error(`Mailbox check failed: ${data.error}`);
      else toast.success(`Checked ${data.checked} new message(s) — ${data.new_alerts} legal-risk alert(s)`);
      await load();
    } finally { setChecking(false); }
  };

  const setAlertStatus = async (id, s) => {
    await api.patch(`/admin/legal-alerts/${id}`, { status: s });
    setAlerts((as) => as.map((a) => (a.id === id ? { ...a, status: s } : a)));
    toast.success(`Marked ${s}`);
  };

  if (!status || !alerts) return <p className="text-slate-400 font-mono text-sm">Loading...</p>;

  return (
    <div className="space-y-5" data-testid="legal-alerts">
      <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-wrap items-start justify-between gap-4" data-testid="legal-monitor-status">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 flex items-center gap-1.5"><Scale size={13} /> Legal-threat mailbox monitor</p>
          <p className="text-sm text-slate-700 mt-1.5 flex items-center gap-2">
            <Mail size={14} className="text-slate-400" /> {status.mailbox} via {status.host} · every {status.poll_minutes} min
            {status.configured ? <span className="text-[11px] font-mono uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">connected</span>
              : <span data-testid="legal-monitor-unconfigured" className="text-[11px] font-mono uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">password not configured</span>}
          </p>
          <p className="text-xs text-slate-400 mt-1">Last check: {status.last_run ? new Date(status.last_run).toLocaleString() : "never"}{status.last_error ? ` · error: ${status.last_error}` : ""}</p>
          <p className="text-xs text-slate-400 mt-1">Watching for: {status.risk_terms.join(", ")}. Matches get a Section 230 reply saved as a <b>Draft</b> in the mailbox (never sent automatically) plus an email alert.</p>
        </div>
        <Button data-testid="legal-check-now" onClick={checkNow} disabled={checking || !status.configured} variant="outline" className="rounded-full gap-2"><RefreshCw size={15} className={checking ? "animate-spin" : ""} /> Check now</Button>
      </div>

      {alerts.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-14 text-center" data-testid="legal-alerts-empty">
          <CheckCircle2 className="mx-auto text-emerald-300 mb-3" size={40} />
          <p className="text-slate-500">No legal-risk emails detected.</p>
        </div>
      ) : alerts.map((a) => (
        <div key={a.id} data-testid={`legal-alert-${a.id}`} className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span data-testid={`legal-status-${a.id}`} className={`text-[11px] font-mono uppercase tracking-wider border rounded-full px-2 py-0.5 ${CLS[a.status] || CLS.new}`}>{a.status}</span>
                {a.matched_terms.map((t) => <span key={t} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-500 text-white flex items-center gap-1"><AlertTriangle size={10} /> {t}</span>)}
                {!a.draft_saved && <span className="text-[11px] font-mono text-rose-600">draft not saved{a.draft_error ? `: ${a.draft_error}` : ""}</span>}
              </div>
              <h3 className="font-display text-lg text-slate-900 mt-1.5" style={{ fontWeight: 700 }}>{a.subject || "(no subject)"}</h3>
              <p className="text-xs text-slate-500">From {a.from_name ? `${a.from_name} <${a.from_email}>` : a.from_email} · {a.received_at}</p>
            </div>
            <span className="text-xs text-slate-400 font-mono">{new Date(a.created_at).toLocaleString()}</span>
          </div>
          <p className="text-sm text-slate-700 mt-3 leading-relaxed border-l-2 border-slate-200 pl-3">{a.snippet}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid={`legal-links-${a.id}`}>
            <Link2 size={13} className="text-slate-400" />
            {a.linked_supervisors?.length ? a.linked_supervisors.map((s) => (
              <Link key={s.id} to={`/supervisor/${s.id}`} target="_blank" className="text-emerald-700 hover:underline font-medium" data-testid={`legal-linked-sup-${a.id}`}>{s.name} · {s.company}</Link>
            )) : <span className="text-slate-400">No supervisor/review URL found in the email</span>}
            {a.reviews_in_scope > 0 && <span className="text-slate-500">· {a.reviews_in_scope} review(s) in scope{a.reviews_on_hold ? `, ${a.reviews_on_hold} hidden` : ""}</span>}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button size="sm" variant="outline" onClick={() => setOpenDraft(openDraft === a.id ? null : a.id)} className="h-8 rounded-full text-xs gap-1" data-testid={`legal-view-draft-${a.id}`}><FileText size={13} /> {openDraft === a.id ? "Hide draft" : "Review & send draft"}</Button>
            {a.reviews_in_scope > 0 && (a.reviews_on_hold ? (
              <Button size="sm" variant="outline" disabled={busyId === a.id} onClick={() => act(a.id, "release", (d) => `${d.released} review(s) visible again`)} className="h-8 rounded-full text-xs gap-1 border-emerald-300 text-emerald-700" data-testid={`legal-release-${a.id}`}><Eye size={13} /> Release hold</Button>
            ) : (
              <Button size="sm" disabled={busyId === a.id} onClick={() => act(a.id, "hold", (d) => `${d.hidden} review(s) hidden pending review`)} className="h-8 rounded-full text-xs gap-1 bg-rose-600 hover:bg-rose-700" data-testid={`legal-hold-${a.id}`}><EyeOff size={13} /> Hide {a.reviews_in_scope} review(s) pending review</Button>
            ))}
            {STATUSES.filter((s) => s !== a.status).map((s) => (
              <button key={s} data-testid={`legal-set-${s}-${a.id}`} onClick={() => setAlertStatus(a.id, s)} className="text-xs px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 hover:border-emerald-500 hover:text-emerald-700">Mark {s}</button>
            ))}
          </div>
          {openDraft === a.id && (
            <div className="mt-3 space-y-2">
              <Textarea data-testid={`legal-draft-text-${a.id}`} rows={12} value={drafts[a.id] ?? a.sent_text ?? a.draft_text} onChange={(e) => setDrafts((d) => ({ ...d, [a.id]: e.target.value }))} className="text-xs font-mono bg-slate-50" readOnly={a.status === "sent"} />
              {a.status === "sent" ? <p className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 size={13} /> Sent {a.sent_at ? new Date(a.sent_at).toLocaleString() : ""} to {a.from_email}</p> : (
                <div className="flex items-center gap-2">
                  <Button size="sm" disabled={busyId === a.id || !status.configured} onClick={() => act(a.id, "send", () => "Reply sent from support@")} className="h-8 rounded-full text-xs gap-1 bg-slate-900 hover:bg-emerald-600" data-testid={`legal-send-${a.id}`}><Send size={13} /> Send reply to {a.from_email}</Button>
                  {!status.configured && <span className="text-xs text-amber-700">Set the mailbox password to enable sending</span>}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
